import axios from "./axios";
import { handleApiError } from "./handleApiError";

export interface AdminUserSummary {
  id: string;
  username: string;
  email: string;
  isEmailVerified?: boolean;
  role: string;
  roles: string[];
  zTokenBalance: number;
  isBannedAll: boolean;
  isCommentBanned: boolean;
  nameColor?: string;
  avatarFrame?: string;
  phone?: string;
  avatarUrl?: string;
  backgroundUrl?: string;
  level?: number;
  currentExp?: number;
  nextLevelExp?: number;
  totalReads?: number;
  totalComments?: number;
  totalPurchases?: number;
  creatorDailyPartLimit?: number | null;
  excludeFromViewMetrics?: boolean;
  [key: string]: any;
}

export interface AdminUserProgressSummary {
  level: number;
  currentExp: number;
  nextLevelExp: number;
  totalReads: number;
  totalComments: number;
  totalPurchases: number;
}

export interface AdminUserPackageSummary {
  id: string;
  packageId: string;
  packageName: string;
  dailyQuotaPremium?: number;
  dailyQuotaFree?: number;
  durationDays?: number;
  startDateUtc: string;
  endDateUtc: string;
}

export interface AdminUserUnlockSummary {
  id: string;
  contentPartId: string;
  partTitle: string;
  price: number;
  purchasedAt: string;
  source?: string;
  reason?: string;
}

export interface AdminUserTransactionSummary {
  id: string;
  type: string;
  amount: number;
  description?: string;
  source?: string;
  reason?: string;
  createdAt: string;
}

export interface AdminUserAchievementSummary {
  id: string;
  achievementId: string;
  title: string;
  iconUrl?: string;
  earnedAt: string;
  isPinned?: boolean;
}

export interface AdminUserReadLogSummary {
  id: string;
  contentId: string;
  contentPartId: string;
  contentTitle?: string;
  partTitle?: string;
  isPremium: boolean;
  isFreePreview: boolean;
  quotaSource?: string;
  priceCharged?: number | null;
  readAtUtc: string;
}

export interface AdminUserTokenPurchaseSummary {
  id: string;
  tokenAmount: number;
  priceAmount: number;
  currency: string;
  status: string;
  paramOrderId?: string;
  paramTransactionId?: string;
  source?: string;
  reason?: string;
  createdAtUtc: string;
  completedAtUtc?: string | null;
}

export interface AdminUserTransactionFlowSummary {
  totalIncoming: number;
  totalOutgoing: number;
  giftIncoming: number;
  packageSpend: number;
  singleUnlockSpend: number;
  tokenPurchaseLoad: number;
  movementCount: number;
  lastActivityAt?: string | null;
}

export interface AdminUserTransactionBreakdownSummary {
  type: string;
  totalIncoming: number;
  totalOutgoing: number;
  movementCount: number;
  lastActivityAt?: string | null;
}

export interface AdminUserDetail extends AdminUserSummary {
  createdAt: string;
  progress?: AdminUserProgressSummary | null;
  activePackages: AdminUserPackageSummary[];
  packageHistory: AdminUserPackageSummary[];
  recentUnlocks: AdminUserUnlockSummary[];
  recentTransactions: AdminUserTransactionSummary[];
  recentReadLogs: AdminUserReadLogSummary[];
  tokenPurchases: AdminUserTokenPurchaseSummary[];
  transactionSummary?: AdminUserTransactionFlowSummary | null;
  transactionBreakdown: AdminUserTransactionBreakdownSummary[];
  achievements: AdminUserAchievementSummary[];
}

export interface UserListQuery {
  search?: string;
  role?: string;
  sort?: "zAsc" | "zDesc" | "nameAsc" | "nameDesc";
  skip?: number;
  take?: number;
}

export interface UserListResponse {
  items: AdminUserSummary[];
  totalCount: number;
}

export interface AdminUserUpdatePayload {
  name?: string;
  email?: string;
  isEmailVerified?: boolean;
  phone?: string;
  roleName?: string;
  roleNames?: string[];
  zTokenBalance?: number;
  nameColor?: string;
  avatarFrame?: string | null;
  avatarUrl?: string;
  backgroundUrl?: string;
  isBannedAll?: boolean;
  isCommentBanned?: boolean;
  creatorDailyPartLimit?: number | null;
  excludeFromViewMetrics?: boolean;
  [key: string]: any;
}

export interface AdminUserDeleteImpact {
  contents: number;
  comments: number;
  favorites: number;
  purchases: number;
  packages: number;
  readLogs: number;
  notifications: number;
  transactions: number;
  achievements: number;
  libraryEntries: number;
  feedbackTickets: number;
  total: number;
}

export interface AdminDeleteActionPayload {
  hardDelete?: boolean;
  confirmHardDelete?: boolean;
  reason?: string;
}

export interface AdminDeleteActionResult {
  success: boolean;
  message?: string;
  mode?: "soft" | "hard";
  impact?: any;
  linkedReadLogs?: number;
}

export interface AdminGiftZTokenPayload {
  amount: number;
  note?: string;
}

export interface AdminGiftPackagePayload {
  packageId: string;
  durationMultiplier?: number;
  note?: string;
}

export interface AdminTransferContentPayload {
  targetUserId: string;
  note?: string;
}

export interface AdminTransferContentResult {
  success: boolean;
  message?: string;
  contentId?: string;
  newOwnerUserId?: string;
}

export async function getUsersPaginated(query: UserListQuery = {}): Promise<UserListResponse> {
  try {
    const res = await axios.get("/admin/users", {
      params: {
        name: query.search?.trim() || undefined,
        role: query.role?.trim() || undefined,
        sort: query.sort,
        skip: query.skip ?? 0,
        take: query.take ?? 20,
      },
    });

    const data = res.data ?? {};
    const rawItems = Array.isArray(data.items) ? data.items : [];
    const items: AdminUserSummary[] = rawItems.map(normalizeSummary);

    const totalCountRaw = Number(data.totalCount ?? rawItems.length ?? 0);
    const totalCount = Number.isFinite(totalCountRaw) ? totalCountRaw : rawItems.length;

    return { items, totalCount };
  } catch (error) {
    return handleApiError(error, "getUsersPaginated", { items: [], totalCount: 0 });
  }
}

export async function getUserDetail(userId: string): Promise<AdminUserDetail | null> {
  try {
    const res = await axios.get(`/admin/users/${userId}`);
    if (!res.data) {
      return null;
    }

    return normalizeDetail(res.data);
  } catch (error) {
    return handleApiError(error, "getUserDetail", null);
  }
}

export async function updateUserBan(userId: string, payload: { isBannedAll?: boolean; isCommentBanned?: boolean }): Promise<boolean> {
  try {
    const res = await axios.post("/admin/users/ban", {
      userId,
      isBannedAll: payload.isBannedAll,
      isCommentBanned: payload.isCommentBanned,
    });
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "updateUserBan", false);
  }
}

export async function getAllRoles(): Promise<string[]> {
  try {
    const res = await axios.get("/roles");
    if (!Array.isArray(res.data)) {
      return [];
    }

    return res.data
      .map((role: any) => fixText(String(role.name ?? role.Name ?? "")).trim())
      .filter((name: string) => name.length > 0);
  } catch (error) {
    return handleApiError(error, "getAllRoles", []);
  }
}

export async function updateAdminUser(userId: string, payload: AdminUserUpdatePayload): Promise<AdminUserDetail | null> {
  try {
    const res = await axios.put(`/admin/users/${userId}`, payload);
    if (!res.data) {
      return null;
    }

    return normalizeDetail(res.data);
  } catch (error) {
    return handleApiError(error, "updateAdminUser", null);
  }
}

export async function getUserDeleteImpact(userId: string): Promise<AdminUserDeleteImpact | null> {
  try {
    const response = await axios.get(`/admin/users/${userId}/delete-impact`);
    return normalizeDeleteImpact(response.data?.impact);
  } catch (error) {
    return handleApiError(error, "getUserDeleteImpact", null);
  }
}

export async function deleteAdminUser(
  userId: string,
  payload: AdminDeleteActionPayload = {},
): Promise<AdminDeleteActionResult> {
  try {
    const response = await axios.post(`/admin/users/${userId}/delete`, payload);
    return normalizeDeleteAction(response.data);
  } catch (error) {
    return handleApiError(error, "deleteAdminUser", {
      success: false,
      message: "Kullanici silinemedi.",
    });
  }
}

export async function deleteUserPackageAssignment(
  userId: string,
  userPackageId: string,
  payload: AdminDeleteActionPayload = {},
): Promise<AdminDeleteActionResult> {
  try {
    const response = await axios.post(`/admin/users/${userId}/packages/${userPackageId}/delete`, payload);
    return normalizeDeleteAction(response.data);
  } catch (error) {
    return handleApiError(error, "deleteUserPackageAssignment", {
      success: false,
      message: "Kullanici paketi silinemedi.",
    });
  }
}

export async function giftUserZToken(userId: string, payload: AdminGiftZTokenPayload): Promise<boolean> {
  try {
    const response = await axios.post(`/admin/users/${userId}/gift/ztoken`, payload);
    return Boolean(response.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "giftUserZToken", false);
  }
}

export async function giftUserPackage(userId: string, payload: AdminGiftPackagePayload): Promise<boolean> {
  try {
    const response = await axios.post(`/admin/users/${userId}/gift/package`, payload);
    return Boolean(response.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "giftUserPackage", false);
  }
}

export async function transferContentOwnership(
  contentId: string,
  payload: AdminTransferContentPayload,
): Promise<AdminTransferContentResult> {
  try {
    const response = await axios.post(`/admin/users/content/${contentId}/transfer`, payload);
    return {
      success: Boolean(response.data?.success ?? true),
      message: typeof response.data?.message === "string" ? response.data.message : undefined,
      contentId: typeof response.data?.contentId === "string" ? response.data.contentId : undefined,
      newOwnerUserId: typeof response.data?.newOwnerUserId === "string" ? response.data.newOwnerUserId : undefined,
    };
  } catch (error) {
    return handleApiError(error, "transferContentOwnership", {
      success: false,
      message: "Seri sahipligi guncellenemedi.",
    });
  }
}

export async function resendUserEmailVerificationFromAdmin(
  userId: string,
): Promise<{ success: boolean; message: string; nextAllowedAtUtc?: string | null }> {
  try {
    const response = await axios.post(`/admin/users/${userId}/email-verification/resend`);
    return {
      success: Boolean(response.data?.success ?? true),
      message: String(response.data?.message ?? "Dogrulama e-postasi gonderildi."),
      nextAllowedAtUtc: response.data?.nextAllowedAtUtc ?? null,
    };
  } catch (error) {
    return handleApiError(error, "resendUserEmailVerificationFromAdmin", {
      success: false,
      message: "Dogrulama e-postasi gonderilemedi.",
      nextAllowedAtUtc: null,
    });
  }
}

function normalizeSummary(item: any): AdminUserSummary {
  const roles = normalizeRoles(item);
  const primaryRoleRaw = item.role ?? item.Role ?? roles[0] ?? "?ye";
  const primaryRole =
    typeof primaryRoleRaw === "string" && primaryRoleRaw.trim().length > 0
      ? fixText(primaryRoleRaw)
      : "?ye";

  if (!roles.length) {
    roles.push(primaryRole);
  }

  return {
    id: String(item.id ?? item.Id),
    username: fixText(String(item.username ?? item.Username ?? item.name ?? "")),
    email: fixText(String(item.email ?? item.Email ?? "")),
    isEmailVerified: Boolean(item.isEmailVerified ?? item.IsEmailVerified ?? false),
    role: primaryRole,
    roles: Array.from(new Set(roles)),
    zTokenBalance: Number(item.zTokenBalance ?? item.ZTokenBalance ?? 0),
    isBannedAll: Boolean(item.isBannedAll ?? item.IsBannedAll ?? false),
    isCommentBanned: Boolean(item.isCommentBanned ?? item.IsCommentBanned ?? false),
    nameColor: item.nameColor ?? item.NameColor ?? undefined,
    avatarFrame: item.avatarFrame ?? item.AvatarFrame ?? undefined,
    phone: fixText(item.phone ?? item.Phone ?? "") || undefined,
    avatarUrl: item.avatarUrl ?? item.AvatarUrl ?? undefined,
    backgroundUrl: item.backgroundUrl ?? item.BackgroundUrl ?? undefined,
    level: item.level ?? item.Level ?? undefined,
    currentExp: item.currentExp ?? item.CurrentExp ?? undefined,
    nextLevelExp: item.nextLevelExp ?? item.NextLevelExp ?? undefined,
    totalReads: item.totalReads ?? item.TotalReads ?? undefined,
    totalComments: item.totalComments ?? item.TotalComments ?? undefined,
    totalPurchases: item.totalPurchases ?? item.TotalPurchases ?? undefined,
    creatorDailyPartLimit: normalizeNullableNumber(item.creatorDailyPartLimit ?? item.CreatorDailyPartLimit),
    excludeFromViewMetrics: Boolean(item.excludeFromViewMetrics ?? item.ExcludeFromViewMetrics ?? false),
  };
}

function normalizeDetail(data: any): AdminUserDetail {
  const progressRaw = data.progress;
  const progress: AdminUserProgressSummary | null = progressRaw
    ? {
        level: Number(progressRaw.level ?? progressRaw.Level ?? 0),
        currentExp: Number(progressRaw.currentExp ?? progressRaw.CurrentExp ?? 0),
        nextLevelExp: Number(progressRaw.nextLevelExp ?? progressRaw.NextLevelExp ?? 1),
        totalReads: Number(progressRaw.totalReads ?? progressRaw.TotalReads ?? 0),
        totalComments: Number(progressRaw.totalComments ?? progressRaw.TotalComments ?? 0),
        totalPurchases: Number(progressRaw.totalPurchases ?? progressRaw.TotalPurchases ?? 0),
      }
    : null;

  return {
    ...normalizeSummary(data),
    createdAt: data.createdAt ?? data.CreatedAt ?? "",
    progress,
    activePackages: Array.isArray(data.activePackages) ? data.activePackages.map(normalizePackage) : [],
    packageHistory: Array.isArray(data.packageHistory) ? data.packageHistory.map(normalizePackage) : [],
    recentUnlocks: Array.isArray(data.recentUnlocks) ? data.recentUnlocks.map(normalizeUnlock) : [],
    recentTransactions: Array.isArray(data.recentTransactions) ? data.recentTransactions.map(normalizeTransaction) : [],
    recentReadLogs: Array.isArray(data.recentReadLogs) ? data.recentReadLogs.map(normalizeReadLog) : [],
    tokenPurchases: Array.isArray(data.tokenPurchases) ? data.tokenPurchases.map(normalizeTokenPurchase) : [],
    transactionSummary:data.transactionSummary ? normalizeTransactionSummary(data.transactionSummary) : null,
    transactionBreakdown: Array.isArray(data.transactionBreakdown)
      ? data.transactionBreakdown.map(normalizeTransactionBreakdown)
      : [],
    achievements: Array.isArray(data.achievements) ? data.achievements.map(normalizeAchievement) : [],
  };
}

function normalizePackage(item: any): AdminUserPackageSummary {
  return {
    id: String(item.id ?? item.Id),
    packageId: String(item.packageId ?? item.PackageId ?? ""),
    packageName: fixText(item.packageName ?? item.PackageName ?? ""),
    dailyQuotaPremium: item.dailyQuotaPremium ?? item.DailyQuotaPremium ?? undefined,
    dailyQuotaFree: item.dailyQuotaFree ?? item.DailyQuotaFree ?? undefined,
    durationDays: item.durationDays ?? item.DurationDays ?? undefined,
    startDateUtc: item.startDateUtc ?? item.StartDateUtc ?? "",
    endDateUtc: item.endDateUtc ?? item.EndDateUtc ?? "",
  };
}

function normalizeUnlock(item: any): AdminUserUnlockSummary {
  return {
    id: String(item.id ?? item.Id),
    contentPartId: String(item.contentPartId ?? item.ContentPartId ?? ""),
    partTitle: fixText(item.partTitle ?? item.PartTitle ?? ""),
    price: Number(item.price ?? item.Price ?? 0),
    purchasedAt: item.purchasedAt ?? item.PurchasedAt ?? "",
    source: fixText(item.source ?? item.Source ?? "") || undefined,
    reason: fixText(item.reason ?? item.Reason ?? "") || undefined,
  };
}

function normalizeTransaction(item: any): AdminUserTransactionSummary {
  return {
    id: String(item.id ?? item.Id),
    type: fixText(item.type ?? item.Type ?? ""),
    amount: Number(item.amount ?? item.Amount ?? 0),
    description: fixText(item.description ?? item.Description ?? "") || undefined,
    source: fixText(item.source ?? item.Source ?? "") || undefined,
    reason: fixText(item.reason ?? item.Reason ?? "") || undefined,
    createdAt: item.createdAt ?? item.CreatedAt ?? "",
  };
}

function normalizeReadLog(item: any): AdminUserReadLogSummary {
  return {
    id: String(item.id ?? item.Id),
    contentId: String(item.contentId ?? item.ContentId ?? ""),
    contentPartId: String(item.contentPartId ?? item.ContentPartId ?? ""),
    contentTitle: fixText(item.contentTitle ?? item.ContentTitle ?? "") || undefined,
    partTitle: fixText(item.partTitle ?? item.PartTitle ?? "") || undefined,
    isPremium: Boolean(item.isPremium ?? item.IsPremium ?? false),
    isFreePreview: Boolean(item.isFreePreview ?? item.IsFreePreview ?? false),
    quotaSource: fixText(item.quotaSource ?? item.QuotaSource ?? "") || undefined,
    priceCharged: item.priceCharged ?? item.PriceCharged ?? null,
    readAtUtc: item.readAtUtc ?? item.ReadAtUtc ?? "",
  };
}

function normalizeTokenPurchase(item: any): AdminUserTokenPurchaseSummary {
  return {
    id: String(item.id ?? item.Id),
    tokenAmount: Number(item.tokenAmount ?? item.TokenAmount ?? 0),
    priceAmount: Number(item.priceAmount ?? item.PriceAmount ?? 0),
    currency: fixText(item.currency ?? item.Currency ?? "TRY"),
    status: fixText(item.status ?? item.Status ?? "pending"),
    paramOrderId: fixText(item.paramOrderId ?? item.ParamOrderId ?? "") || undefined,
    paramTransactionId: fixText(item.paramTransactionId ?? item.ParamTransactionId ?? "") || undefined,
    source: fixText(item.source ?? item.Source ?? "") || undefined,
    reason: fixText(item.reason ?? item.Reason ?? "") || undefined,
    createdAtUtc: item.createdAtUtc ?? item.CreatedAtUtc ?? "",
    completedAtUtc: item.completedAtUtc ?? item.CompletedAtUtc ?? null,
  };
}

function normalizeTransactionSummary(item: any): AdminUserTransactionFlowSummary {
  return {
    totalIncoming: Number(item.totalIncoming ?? item.TotalIncoming ?? 0),
    totalOutgoing: Number(item.totalOutgoing ?? item.TotalOutgoing ?? 0),
    giftIncoming: Number(item.giftIncoming ?? item.GiftIncoming ?? 0),
    packageSpend: Number(item.packageSpend ?? item.PackageSpend ?? 0),
    singleUnlockSpend: Number(item.singleUnlockSpend ?? item.SingleUnlockSpend ?? 0),
    tokenPurchaseLoad: Number(item.tokenPurchaseLoad ?? item.TokenPurchaseLoad ?? 0),
    movementCount: Number(item.movementCount ?? item.MovementCount ?? 0),
    lastActivityAt: item.lastActivityAt ?? item.LastActivityAt ?? null,
  };
}

function normalizeTransactionBreakdown(item: any): AdminUserTransactionBreakdownSummary {
  return {
    type: fixText(item.type ?? item.Type ?? ""),
    totalIncoming: Number(item.totalIncoming ?? item.TotalIncoming ?? 0),
    totalOutgoing: Number(item.totalOutgoing ?? item.TotalOutgoing ?? 0),
    movementCount: Number(item.movementCount ?? item.MovementCount ?? 0),
    lastActivityAt: item.lastActivityAt ?? item.LastActivityAt ?? null,
  };
}

function normalizeAchievement(item: any): AdminUserAchievementSummary {
  return {
    id: String(item.id ?? item.Id),
    achievementId: String(item.achievementId ?? item.AchievementId ?? ""),
    title: fixText(item.title ?? item.Title ?? ""),
    iconUrl: item.iconUrl ?? item.IconUrl ?? undefined,
    earnedAt: item.earnedAt ?? item.EarnedAt ?? "",
    isPinned: Boolean(item.isPinned ?? item.IsPinned ?? false),
  };
}

function normalizeNullableNumber(value: any): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizeRoles(data: any): string[] {
  const raw = data?.roles ?? data?.Roles;
  const roles: string[] = [];

  if (Array.isArray(raw)) {
    raw.forEach((role) => {
      const normalized = fixText(String(role ?? "")).trim();
      if (normalized.length > 0) {
        roles.push(normalized);
      }
    });
  }

  const fallback = data?.role ?? data?.Role;
  if ((!Array.isArray(raw) || roles.length === 0) && typeof fallback === "string" && fallback.trim().length > 0) {
    roles.push(fixText(fallback.trim()));
  }

  return roles;
}

function normalizeDeleteAction(payload: any): AdminDeleteActionResult {
  const linkedReadLogsRaw = payload?.impact?.linkedReadLogs ?? payload?.linkedReadLogs;
  const linkedReadLogs = Number(linkedReadLogsRaw);
  return {
    success: Boolean(payload?.success),
    message: typeof payload?.message === "string" ? fixText(payload.message) : undefined,
    mode: payload?.mode === "hard" ? "hard" : payload?.mode === "soft" ? "soft" : undefined,
    impact: payload?.impact,
    linkedReadLogs: Number.isFinite(linkedReadLogs) ? linkedReadLogs : undefined,
  };
}

function normalizeDeleteImpact(raw: any): AdminUserDeleteImpact | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  return {
    contents: Number(raw.contents ?? 0),
    comments: Number(raw.comments ?? 0),
    favorites: Number(raw.favorites ?? 0),
    purchases: Number(raw.purchases ?? 0),
    packages: Number(raw.packages ?? 0),
    readLogs: Number(raw.readLogs ?? 0),
    notifications: Number(raw.notifications ?? 0),
    transactions: Number(raw.transactions ?? 0),
    achievements: Number(raw.achievements ?? 0),
    libraryEntries: Number(raw.libraryEntries ?? 0),
    feedbackTickets: Number(raw.feedbackTickets ?? 0),
    total: Number(raw.total ?? 0),
  };
}

function fixText(value: string): string {
  if (!value) {
    return "";
  }

  let result = value;
  for (let i = 0; i < 2; i += 1) {
    if (!/[\u00C3\u00C2\u00C4\u00C5\u00E2]/.test(result)) {
      break;
    }

    try {
      const bytes = Uint8Array.from(Array.from(result).map((char) => char.charCodeAt(0) & 0xff));
      const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
      if (!decoded.includes("\uFFFD") && decoded !== result) {
        result = decoded;
      } else {
        break;
      }
    } catch {
      break;
    }
  }

  return result.replace(/\uFFFD/g, "");
}
